import { initializeApp } from 'firebase/app'
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useSelector, shallowEqual } from 'react-redux'

import { useLazyGetUserAuthTokenQuery } from 'api/auth/getUserAuthToken'
import { useAuthValue } from 'auth/AuthContext'
import { handleError, recordFirestoreMintState } from 'utils/error'
import { get } from 'utils/lodash'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
}

export const handleLogout = () => {
  const auth = getAuth()
  signOut(auth)
}

const app = initializeApp(firebaseConfig)

export const db = getFirestore(app)

const WithFirebase = ({ children }) => {
  const [getUserAuthToken] = useLazyGetUserAuthTokenQuery()
  const { currentUser } = useAuthValue() || {}
  const selectedbusinessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)

  const handleSignInWithCustomToken = async customToken => {
    const auth = getAuth()
    try {
      await signInWithCustomToken(auth, customToken)
      recordFirestoreMintState('signed-in')
    } catch (e) {
      recordFirestoreMintState(`sign-in-failed-${e?.code ?? 'unknown'}`)
      console.error(e)
    }
  }

  useEffect(() => {
    // Sign the default (data-project) app in so direct Firebase reads (e.g. Storage downloads)
    // are authorized. Impersonation sessions need this too — the access token is minted for the
    // impersonated business (getUserBusinessIDAsUUID resolves it), and failure here is non-fatal
    // since the CRM app runs on the primary auth token regardless.
    if (currentUser && selectedbusinessId) {
      recordFirestoreMintState('pending')
      getUserAuthToken()
        .unwrap()
        .then(response => handleSignInWithCustomToken(response?.token))
        .catch(error => {
          recordFirestoreMintState(`token-request-failed-${error?.status ?? 'unknown'}`)
          handleError(error)
        })
    }
  }, [currentUser, selectedbusinessId, getUserAuthToken])

  return <>{children}</>
}

WithFirebase.propTypes = {
  children: PropTypes.node,
}

export default WithFirebase
